import React, { useState } from "react";
import PropTypes from "prop-types";
import { Row, Col, Form } from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { omit } from "lodash";
import { Link, useNavigate } from "react-router-dom";
import Radio from "@mui/material/Radio";
import {
  Button,
  CircularProgress,
  FormControlLabel,
  FormLabel,
  RadioGroup,
} from "@mui/material";

import { useRequestOTPMutation } from "apis/OTP";

import Modal from "Components/Common/Modal";
import InputFieldText from "Components/Common/InputFieldText";
import { utilityTunnelUserType } from "common/enums";

import "react-toastify/dist/ReactToastify.css";
import { utilityTunnelHomeRoute, homeRoute } from "Routes/constants";
import { onlyNumberKeys } from "utils";
import VerifyEmailModal from "Components/Common/VerifyEmailModal";
import { otpVerificationType, regexForName } from "common/constant";
import { usePostRegisterEmployeeOrContractorMutation } from "apis/UtilityTunnel";

const requestOTPType = otpVerificationType["Verification"];

const SignupModal = ({ open, toggle }) => {
  const navigate = useNavigate();
  const [isOTPSent, setIsOTPSent] = useState(false);

  const [requestOTP, { isLoading: isRequestOTPLoading }] =
    useRequestOTPMutation();

  const [registerEmployeeOrContractor, { isLoading: isRegistering }] =
    usePostRegisterEmployeeOrContractorMutation();

  const registerValidationSchema = Yup.object({
    firstName: Yup.string()
      .matches(regexForName, "Please enter valid first name")
      // .min(3, "First name must contains more then two characters")
      .required("Please enter your first name"),
    lastName: Yup.string().matches(
      regexForName,
      "Please enter valid last name"
    ),
    // .min(3, "Last name contains more then two characters")
    role: Yup.number(),
    email: Yup.string()
      .email("Please enter valid email")
      .required("Please enter your email"),

    password: Yup.string().required("Please enter your password"),
    mobileNumber: Yup.string()
      .matches(/^\d{10}$/, "Please enter valid phone number")
      .required("Please enter your phone number"),
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      role: utilityTunnelUserType.find((type) => type.label === "Employee")
        .value,
      email: "",
      mobileNumber: "",
      password: "",
      isEmailVerified: false,
    },
    validationSchema: registerValidationSchema,
    onSubmit: () => {
      generateOTP();
    },
  });

  const {
    values: { email, mobileNumber },
    setFieldValue,
  } = formik;

  const generateOTP = async () => {
    try {
      await requestOTP(
        {
          email: encodeURIComponent(email),
          mobileNumber,
          type: requestOTPType,
        },
        "",
        undefined,
        true
      );
      setIsOTPSent(true);
    } catch (error) {
      setIsOTPSent(false);
    }
  };

  return (
    <>
      {(isRequestOTPLoading || isOTPSent) && (
        <VerifyEmailModal
          isOpen={isOTPSent}
          isFireSafety={false}
          isUtilityTunnel={true}
          handleClose={() => setIsOTPSent(false)}
          handleOTPverificationSuccess={async () => {
            try {
              const payload = {
                ...formik.values,
                mobileNumber: `${formik.values.mobileNumber}`,
              };
              const registerObj = omit(payload, ["isEmailVerified"]);
              await registerEmployeeOrContractor(registerObj);
              toggle();
              navigate(
                window.location.href.includes("utility-tunnel")
                  ? utilityTunnelHomeRoute
                  : homeRoute,
                { replace: true }
              );
            } catch (error) {}
            setFieldValue("isEmailVerified", true);
          }}
          handleOTPVerificationFailed={() => {
            setFieldValue("isEmailVerified", false);
          }}
          email={email}
          mobileNumber={mobileNumber}
          isLoading={isRequestOTPLoading}
        />
      )}
      <Modal
        isOpen={open}
        handleClose={toggle}
        className="register-modal-wrapper"
        title="Registration"
      >
        <>
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md={6}>
                <InputFieldText
                  label="First Name*"
                  name="firstName"
                  formik={formik}
                  placeholder="Enter name"
                />
              </Col>
              <Col md={6}>
                <InputFieldText
                  name="lastName"
                  placeholder="Enter Last Name"
                  label="Last Name"
                  formik={formik}
                />
              </Col>
              <Row className="mb-3">
                <Col className="d-flex align-items-center">
                  <FormLabel id="role-type" className="pe-4">
                    User Type
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="role-type"
                    name="role"
                    value={formik.values.role}
                    onChange={formik.handleChange}
                  >
                    <FormControlLabel
                      value={
                        utilityTunnelUserType.find(
                          (type) => type.label === "Employee"
                        ).value
                      }
                      control={<Radio />}
                      label="Employee"
                    />
                    <FormControlLabel
                      value={
                        utilityTunnelUserType.find(
                          (type) => type.label === "Contractor"
                        ).value
                      }
                      control={<Radio />}
                      label="Contractor"
                    />
                  </RadioGroup>
                </Col>
              </Row>
              <Col md={6}>
                <InputFieldText
                  label="Email*"
                  name="email"
                  formik={formik}
                  type="email"
                  placeholder="Enter Email"
                />
              </Col>
              <Col md={6}>
                <InputFieldText
                  name="mobileNumber"
                  type="number"
                  InputProps={{
                    inputProps: { min: 0, onKeyDown: onlyNumberKeys },
                  }}
                  placeholder="Enter Phone Number"
                  label="Phone Number*"
                  formik={formik}
                />
              </Col>
              <Col sm={6}>
                <InputFieldText
                  name="password"
                  type="password"
                  placeholder="Enter Password"
                  autoComplete="new-password"
                  label="Password*"
                  formik={formik}
                />
              </Col>
            </Row>
            <Button
              variant="contained"
              className="modal-btn semibold-font"
              type="submit"
              disabled={isRegistering}
            >
              Register
              {isRegistering && <CircularProgress size={20} />}
            </Button>
          </Form>
          <p className="regular-font pt-5 text-center">
            Already have an account ?
            <Link
              to={homeRoute}
              className="medium-font ps-2 sign-in-text"
              onClick={toggle}
            >
              Log In
            </Link>
          </p>
        </>
      </Modal>
    </>
  );
};

SignupModal.propTypes = {
  open: PropTypes.bool,
  toggle: PropTypes.func,
};

export default SignupModal;
